/** @jsx jsx */
import { jsx, Container, Box } from 'theme-ui';
import * as React from 'react';
import {
  Section,
  SectionComponent,
} from '@codersclan/gatsby-theme-airfleet/src/@types';
import { useQueryParam, StringParam } from 'use-query-params';

import Divisor from '../../components/divisor';
import CategorySelector from '../../components/partnerCategorySelector';
import CustomGrid from '../../components/customGrid';
import Card from '../../components/card';
import CTAButton from '../../components/ctaButton';
import { PartnerCategorySelectorOptions } from '../../components/partnerCategorySelector/partnerCategorySelector';

import { getCTA } from '../../helpers/partnerSection';
import usePartnerDefaultImage from '../../hooks/usePartnerDefaultImage';
import usePagination from '../../hooks/usePagination';
import usePartners from '../../hooks/usePartners';

interface Props extends Section {
  background: {
    bgColor: string;
  };
  loadMore: {
    label: string;
  };
  selector: {
    title: string;
  };
  filter: {
    filter: string;
  };
}

const PartnersCards: SectionComponent<Props> = ({
  background,
  loadMore,
  selector,
  filter,
}) => {
  const partners = usePartners(filter?.filter);
  const defaultImage = usePartnerDefaultImage(filter?.filter);

  const { limit, resetPagination, loadMore: handleLoadMore } = usePagination(6);
  // Hooks
  const [currentCat] = useQueryParam('cat', StringParam);

  // Memos
  const partnersCategories = React.useMemo(() => {
    const output: Array<PartnerCategorySelectorOptions> = [];

    partners.forEach(p =>
      p.partnerCategories.nodes.forEach(category => {
        if (
          category &&
          !output.some(
            existingCategory => existingCategory.slug === category.slug,
          )
        ) {
          output.push({
            label: category.name || '',
            value: category.slug,
            slug: category.slug,
          });
        }
      }),
    );

    return output;
  }, [partners]);

  const filteredPartners = React.useMemo(() => {
    if (!currentCat) return [];
    if (currentCat === 'all') return partners;
    return partners.filter(p =>
      p.partnerCategories.nodes.some(c => c.slug === currentCat),
    );
  }, [partners, currentCat]);

  const [partnersToShow, canShowMore] = React.useMemo(
    () => [filteredPartners.slice(0, limit), limit < filteredPartners.length],
    [filteredPartners, limit],
  );

  // Effects
  React.useEffect(() => {
    resetPagination();
  }, [currentCat]);

  return (
    <Box
      sx={{
        backgroundColor: background.bgColor || 'transparent',
      }}
    >
      <Container
        sx={{
          padding: ['0px 24px 60px', '0px 0px 100px'],
        }}
      >
        <Divisor />

        {/* Page Content */}
        <CategorySelector
          options={partnersCategories}
          label={selector?.title}
        />

        <CustomGrid sx={{ marginTop: ['64px', '88px'] }}>
          {partnersToShow.map(partnerCard => {
            const cta = getCTA(partnerCard?.templatePartners?.partnerSection);
            const noLink = !cta.url && !cta.pageSelector;
            return (
              <Card
                key={partnerCard.id}
                category=""
                title={partnerCard.title}
                content={partnerCard.content}
                imgContained
                cta={cta}
                image={
                  partnerCard.featuredImage?.node || {
                    localFile: defaultImage,
                  }
                }
                noLink={noLink}
              />
            );
          })}
        </CustomGrid>

        {canShowMore && (
          <CTAButton
            key={partnersToShow.length}
            type="button"
            alternative
            sx={{ margin: ['57px auto 0px', '84px auto 0px'] }}
            onClick={handleLoadMore}
          >
            {loadMore?.label}
          </CTAButton>
        )}
      </Container>
    </Box>
  );
};

export default PartnersCards;
